import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "./useOrganization";

export interface TeamPermissions {
  can_view_quotes: boolean;
  can_manage_quotes: boolean;
  can_view_invoices: boolean;
  can_manage_invoices: boolean;
  can_view_payments: boolean;
  can_view_reports: boolean;
  can_view_clients: boolean;
  can_manage_clients: boolean;
  can_view_projects: boolean;
  can_manage_projects: boolean;
  can_view_appointments: boolean;
  can_manage_appointments: boolean;
  can_view_messages: boolean;
  can_manage_messages: boolean;
  can_view_services: boolean;
  can_manage_services: boolean;
  can_view_settings: boolean;
  can_manage_settings: boolean;
}

const FULL_ACCESS: TeamPermissions = {
  can_view_quotes: true,
  can_manage_quotes: true,
  can_view_invoices: true,
  can_manage_invoices: true,
  can_view_payments: true,
  can_view_reports: true,
  can_view_clients: true,
  can_manage_clients: true,
  can_view_projects: true,
  can_manage_projects: true,
  can_view_appointments: true,
  can_manage_appointments: true,
  can_view_messages: true,
  can_manage_messages: true,
  can_view_services: true,
  can_manage_services: true,
  can_view_settings: true,
  can_manage_settings: true,
};

/**
 * Returns the current user's team permissions for their active organization.
 * Owners and admins always get full access.
 * Members get permissions from the team_member_permissions table.
 * If no permissions row exists for a member, they get full access (backwards compatibility).
 */
export function useTeamPermissions() {
  const { organizationId, role, isLoading: orgLoading } = useOrganization();

  const isOwnerOrAdmin = role === "owner" || role === "admin";

  const { data: permissions, isLoading: permLoading } = useQuery({
    queryKey: ["team-permissions", organizationId],
    queryFn: async () => {
      if (isOwnerOrAdmin) return FULL_ACCESS;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !organizationId) return FULL_ACCESS;

      const { data, error } = await supabase
        .from("team_member_permissions")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // No permissions row = full access (backwards compatible)
      if (!data) return FULL_ACCESS;

      return {
        can_view_quotes: data.can_view_quotes,
        can_manage_quotes: data.can_manage_quotes,
        can_view_invoices: data.can_view_invoices,
        can_manage_invoices: data.can_manage_invoices,
        can_view_payments: data.can_view_payments,
        can_view_reports: data.can_view_reports,
        can_view_clients: data.can_view_clients,
        can_manage_clients: data.can_manage_clients,
        can_view_projects: data.can_view_projects,
        can_manage_projects: data.can_manage_projects,
        can_view_appointments: data.can_view_appointments,
        can_manage_appointments: data.can_manage_appointments,
        can_view_messages: data.can_view_messages,
        can_manage_messages: data.can_manage_messages,
        can_view_services: data.can_view_services,
        can_manage_services: data.can_manage_services,
        can_view_settings: data.can_view_settings,
        can_manage_settings: data.can_manage_settings,
      } as TeamPermissions;
    },
    enabled: !!organizationId && !orgLoading,
  });

  return {
    permissions: permissions ?? FULL_ACCESS,
    isOwnerOrAdmin,
    isLoading: orgLoading || permLoading,
  };
}

/** Permission groups for UI display */
export const PERMISSION_GROUPS = [
  {
    label: "Financial",
    description: "Quotes, invoices, payments & reports",
    permissions: [
      { key: "can_view_quotes", label: "View Quotes" },
      { key: "can_manage_quotes", label: "Create/Edit Quotes" },
      { key: "can_view_invoices", label: "View Invoices" },
      { key: "can_manage_invoices", label: "Create/Edit Invoices" },
      { key: "can_view_payments", label: "View Payments" },
      { key: "can_view_reports", label: "View Reports" },
    ],
  },
  {
    label: "Clients",
    description: "Client list and contact details",
    permissions: [
      { key: "can_view_clients", label: "View Clients" },
      { key: "can_manage_clients", label: "Add/Edit Clients" },
    ],
  },
  {
    label: "Projects",
    description: "Project tracking and management",
    permissions: [
      { key: "can_view_projects", label: "View Projects" },
      { key: "can_manage_projects", label: "Manage Projects" },
    ],
  },
  {
    label: "Appointments & Messages",
    description: "Calendar and client communication",
    permissions: [
      { key: "can_view_appointments", label: "View Appointments" },
      { key: "can_manage_appointments", label: "Manage Appointments" },
      { key: "can_view_messages", label: "View Messages" },
      { key: "can_manage_messages", label: "Send Messages" },
    ],
  },
  {
    label: "Services",
    description: "Service catalog management",
    permissions: [
      { key: "can_view_services", label: "View Services" },
      { key: "can_manage_services", label: "Add/Edit Services" },
    ],
  },
  {
    label: "Settings",
    description: "Organization settings and billing",
    permissions: [
      { key: "can_view_settings", label: "View Settings" },
      { key: "can_manage_settings", label: "Change Settings" },
    ],
  },
] as const;
